import React, { useEffect, useState } from "react";
import { CaretDown, Check, ArrowSquareOut } from "@phosphor-icons/react";
import { cn } from "@/lib/format";
import { NETWORK_LINKS } from "@/lib/constants";

// Header network switcher — Mainnet / PreProd, matching Cardanoscan's pattern:
// each network is a separate deployment, the dropdown just links between them.
export const NetworkSwitcher = () => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef(null);
  const current = NETWORK_LINKS.find((n) => n.active) || NETWORK_LINKS[0];
  const isMainnet = current.key === "mainnet";

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        data-testid="network-switcher-button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "inline-flex items-center gap-2 border px-2.5 py-1 rounded-md transition-colors",
          isMainnet
            ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-400 hover:bg-emerald-500/10"
            : "border-yellow-500/40 bg-yellow-500/5 text-yellow-400 hover:bg-yellow-500/10",
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        title="Served from a Cardano db-sync database — see network details"
      >
        <span className={cn("w-1.5 h-1.5 rounded-full animate-pulse", isMainnet ? "bg-emerald-400" : "bg-yellow-400")} />
        {current.label}
        <CaretDown size={11} className={cn("transition-transform", open && "rotate-180")} />
      </button>
      {open ? (
        <div
          data-testid="network-switcher-dropdown"
          role="listbox"
          className="absolute right-0 top-full mt-2 z-[60] min-w-[220px] bg-card/95 backdrop-blur-md border border-primary/30 rounded-lg shadow-xl shadow-primary/10 py-1.5"
        >
          {NETWORK_LINKS.map((n) => {
            const disabled = !n.active && !n.url;
            const content = (
              <>
                <span className="flex flex-col gap-0.5 text-left">
                  <span className="text-[12px] font-medium">{n.label}</span>
                  <span className="text-[10px] mono text-muted-foreground truncate max-w-[150px]">
                    {n.url ? n.url.replace(/^https?:\/\//, "") : disabled ? "not deployed yet" : ""}
                  </span>
                </span>
                {n.active ? (
                  <Check size={13} className="text-primary shrink-0" />
                ) : n.url ? (
                  <ArrowSquareOut size={13} className="text-muted-foreground shrink-0" />
                ) : null}
              </>
            );
            const className = cn(
              "w-full flex items-center justify-between gap-3 px-3 py-2 transition-colors",
              n.active ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted/30",
              disabled && "opacity-50 cursor-not-allowed",
            );
            if (n.active || disabled) {
              return (
                <div key={n.key} data-testid={`network-option-${n.key}`} className={className}>
                  {content}
                </div>
              );
            }
            return (
              <a
                key={n.key}
                data-testid={`network-option-${n.key}`}
                href={n.url}
                className={className}
              >
                {content}
              </a>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};
